
import React, { useState, useEffect, useCallback, useRef } from 'react';

const ALL_ICONS = [
  'fa-rocket', 'fa-ghost', 'fa-dragon', 'fa-microchip',
  'fa-vial', 'fa-biohazard', 'fa-atom', 'fa-brain',
  'fa-meteor', 'fa-satellite', 'fa-robot', 'fa-user-astronaut'
];

const COLORS = [
  '#f472b6', '#60a5fa', '#34d399', '#fbbf24', 
  '#a78bfa', '#f87171', '#818cf8', '#2dd4bf',
  '#fb923c', '#c084fc', '#4ade80', '#38bdf8'
];

interface Card {
  id: number;
  icon: string;
  isFlipped: boolean;
  isMatched: boolean;
  color: string;
}

enum Difficulty {
  Easy = 'Easy',
  Medium = 'Medium',
  Hard = 'Hard'
}

const DIFFICULTY_CONFIG = {
  [Difficulty.Easy]: { pairs: [3, 3, 4, 4, 5], multiplier: 1, label: 'Easy' },
  [Difficulty.Medium]: { pairs: [4, 5, 6, 7, 8], multiplier: 1.5, label: 'Medium' },
  [Difficulty.Hard]: { pairs: [6, 7, 8, 9, 10], multiplier: 2.5, label: 'Hard' }
};

const MemoryMatrix: React.FC<{ onGameOver: (s: number) => void; isPlaying: boolean; sfxVolume: number; hapticFeedback: boolean }> = ({ onGameOver, isPlaying, sfxVolume, hapticFeedback }) => {
  const [difficulty, setDifficulty] = useState<Difficulty | null>(null);
  const [level, setLevel] = useState(1);
  const [puzzleCount, setPuzzleCount] = useState(0);
  const [cards, setCards] = useState<Card[]>([]);
  const [flipped, setFlipped] = useState<number[]>([]);
  const [score, setScore] = useState(0);
  const [moves, setMoves] = useState(0);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isLevelClearing, setIsLevelClearing] = useState(false);

  const previewTimeoutRef = useRef<number | null>(null);

  const playSfx = useCallback((src: string, volume: number) => {
    if (volume > 0) {
      try {
        const audio = new Audio(src);
        audio.volume = volume;
        audio.play().catch(() => {});
      } catch (e) {}
    }
  }, []);

  const triggerHapticFeedback = useCallback(() => {
    if (hapticFeedback && navigator.vibrate) {
      navigator.vibrate(50);
    }
  }, [hapticFeedback]);

  const getPairsForLevel = useCallback((lvl: number, diff: Difficulty) => {
    const config = DIFFICULTY_CONFIG[diff];
    const index = Math.min(lvl - 1, config.pairs.length - 1);
    return config.pairs[index];
  }, []);

  const initLevel = useCallback((lvl: number, diff: Difficulty) => {
    if (previewTimeoutRef.current) window.clearTimeout(previewTimeoutRef.current);
    
    setIsLevelClearing(false);
    setIsPreviewing(true);
    const numPairs = getPairsForLevel(lvl, diff);
    const selectedIcons = ALL_ICONS.slice(0, numPairs);
    const pairIcons = [...selectedIcons, ...selectedIcons];
    
    const newCards = pairIcons
      .sort(() => Math.random() - 0.5)
      .map((icon, index) => ({
        id: index,
        icon,
        isFlipped: true, 
        isMatched: false,
        color: COLORS[ALL_ICONS.indexOf(icon)]
      }));

    setCards(newCards);
    setFlipped([]);
    
    previewTimeoutRef.current = window.setTimeout(() => {
      setCards(prev => prev.map(c => ({ ...c, isFlipped: false })));
      setIsPreviewing(false);
    }, 2500);
  }, [getPairsForLevel]);

  useEffect(() => {
    if (isPlaying && difficulty) {
      setLevel(1);
      setPuzzleCount(0);
      setScore(0);
      setMoves(0);
      initLevel(1, difficulty);
    } else if (!isPlaying) {
      setDifficulty(null);
    }
    return () => {
      if (previewTimeoutRef.current) window.clearTimeout(previewTimeoutRef.current);
    };
  }, [isPlaying, difficulty, initLevel]);

  const handleFlip = (id: number) => {
    if (!difficulty || isPreviewing || isLevelClearing || flipped.length === 2 || cards[id].isFlipped || cards[id].isMatched) return;

    playSfx('/sfx/flip.mp3', sfxVolume);
    triggerHapticFeedback();
    const newFlipped = [...flipped, id];
    setFlipped(newFlipped);
    
    setCards(prev => prev.map(c => c.id === id ? { ...c, isFlipped: true } : c));

    if (newFlipped.length === 2) {
      setMoves(m => m + 1);
      const [first, second] = newFlipped;
      
      if (cards[first].icon === cards[second].icon) {
        setTimeout(() => {
          setCards(prev => {
            const updated = prev.map(c => 
              (c.id === first || c.id === second) ? { ...c, isMatched: true, isFlipped: true } : c
            );
            if (updated.every(c => c.isMatched)) {
              playSfx('/sfx/match.mp3', sfxVolume);
              triggerHapticFeedback();
              handleLevelComplete();
            }
            return updated;
          });
          setFlipped([]);
          const baseScore = 10 * level;
          const multiplier = DIFFICULTY_CONFIG[difficulty].multiplier;
          setScore(s => s + Math.round(baseScore * multiplier));
        }, 500);
      } else {
        setTimeout(() => {
          setCards(prev => prev.map(c => 
            (c.id === first || c.id === second) ? { ...c, isFlipped: false } : c
          ));
          setFlipped([]);
          setScore(s => Math.max(0, s - 10));
        }, 1000);
      }
    }
  };

  const handleLevelComplete = () => {
    if (!difficulty) return;
    setIsLevelClearing(true);
    setTimeout(() => {
      const nextPuzzleCount = puzzleCount + 1;
      if (nextPuzzleCount >= 3) {
        const nextLevel = level + 1;
        if (nextLevel > 5) {
          const bonus = 100 * DIFFICULTY_CONFIG[difficulty].multiplier;
          onGameOver(score + Math.round(bonus));
        } else {
          setLevel(nextLevel);
          setPuzzleCount(0);
          initLevel(nextLevel, difficulty);
        }
      } else {
        setPuzzleCount(nextPuzzleCount);
        initLevel(level, difficulty);
      }
    }, 1500);
  };

  const getGridCols = () => {
    const count = cards.length;
    if (count <= 6) return 'grid-cols-3';
    if (count <= 8) return 'grid-cols-4';
    if (count <= 12) return 'grid-cols-3 md:grid-cols-4';
    if (count <= 16) return 'grid-cols-4';
    return 'grid-cols-4 md:grid-cols-5';
  };

  if (isPlaying && !difficulty) {
    return (
      <div className="flex flex-col items-center justify-center gap-8 w-full max-w-lg px-6 py-12 bg-white/5 backdrop-blur-md rounded-[2.5rem] border border-white/10 shadow-2xl animate-in fade-in zoom-in duration-500">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-teal-500/10 flex items-center justify-center text-teal-500 text-3xl mx-auto mb-4">
            <i className="fas fa-layer-group"></i>
          </div>
          <h2 className="text-3xl font-black text-slate-900 dark:text-white uppercase italic tracking-tighter">Select Difficulty</h2>
          <p className="text-slate-500 text-xs font-bold uppercase tracking-widest mt-1">Calibrate Neural Matrix Complexity</p>
        </div>

        <div className="grid grid-cols-1 gap-4 w-full">
          {Object.values(Difficulty).map((diff) => (
            <button
              key={diff}
              onClick={() => setDifficulty(diff)}
              className="group relative flex items-center justify-between p-6 rounded-2xl bg-white dark:bg-white/5 border-2 border-slate-200 dark:border-white/10 hover:border-teal-500 transition-all hover:scale-[1.02] active:scale-95 overflow-hidden"
            >
              <div className="flex flex-col items-start">
                <span className="text-xl font-black text-slate-800 dark:text-white uppercase italic">{diff}</span>
                <span className="text-[10px] text-slate-500 font-black uppercase tracking-widest">
                  {DIFFICULTY_CONFIG[diff].multiplier}x Juice Multiplier
                </span>
              </div>
              <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center text-teal-500 group-hover:bg-teal-500 group-hover:text-white transition-colors">
                <i className="fas fa-chevron-right"></i>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-6 w-full max-w-lg px-4 py-6 select-none bg-white/5 backdrop-blur-md rounded-[3rem] border border-white/10 shadow-2xl">
      <div className="w-full flex justify-between items-center glass-card p-4 rounded-3xl border-teal-500/20 shadow-xl border-2 transition-colors">
        <div className="flex flex-col">
          <p className="text-[10px] text-slate-500 uppercase font-black tracking-widest">Matrix Level</p>
          <div className="flex items-center gap-2">
             <span className="text-3xl font-black text-teal-500 italic">0{level}</span>
             <div className="flex flex-col gap-1">
               <div className="h-1.5 w-12 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full bg-teal-500" style={{ width: `${(level/5)*100}%` }} />
               </div>
               <div className="flex gap-1">
                 {[0, 1, 2].map(i => (
                   <div key={i} className={`h-1 w-3.5 rounded-full ${i < puzzleCount ? 'bg-teal-500' : 'bg-white/10'}`} />
                 ))}
               </div>
             </div>
          </div>
        </div>
        <div className="text-center">
          <p className="text-[10px] text-slate-500 uppercase font-black tracking-widest">Juice</p>
          <p className="text-3xl font-black text-indigo-600 dark:text-indigo-400 tabular-nums drop-shadow-sm transition-colors">{score.toLocaleString()}</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-slate-500 uppercase font-black tracking-widest">Moves</p>
          <p className="text-2xl font-black text-slate-500 dark:text-slate-400 tabular-nums transition-colors">{moves}</p>
        </div>
      </div>

      <div className="relative w-full aspect-square md:aspect-auto" key={level}>
        {isPreviewing && (
          <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-20 animate-bounce">
            <span className="bg-teal-500 text-white text-[10px] font-black uppercase tracking-[0.3em] px-4 py-1.5 rounded-full shadow-lg">
              Memorize the Matrix!
            </span>
          </div>
        )}

        {isLevelClearing && (
          <div className="absolute inset-0 z-30 flex items-center justify-center bg-slate-900/60 backdrop-blur-md rounded-[2.5rem] animate-in fade-in zoom-in duration-500">
             <div className="text-center">
                <i className="fas fa-check-circle text-6xl text-teal-400 mb-4 animate-bounce"></i>
                <h3 className="text-4xl font-black italic text-white uppercase tracking-tighter">Level {level} Clear!</h3>
                <p className="text-teal-400 font-bold tracking-widest text-xs uppercase mt-2">Uploading next matrix...</p>
             </div>
          </div>
        )}

        <div className={`grid ${getGridCols()} gap-3 w-full`}>
          {cards.map(card => (
            <div 
              key={card.id}
              onClick={() => handleFlip(card.id)}
              className={`
                relative aspect-square rounded-2xl cursor-pointer transition-all duration-500 preserve-3d
                ${card.isFlipped ? '[transform:rotateY(180deg)]' : ''}
                ${card.isMatched ? 'opacity-40 scale-90' : isPreviewing ? '' : 'hover:scale-105 active:scale-95'}
              `}
            >
              <div className="absolute inset-0 backface-hidden flex items-center justify-center bg-slate-800 rounded-2xl border-2 border-white/10 shadow-lg group overflow-hidden">
                <div className="absolute inset-0 bg-grid-white/[0.02]" />
                <div className="w-8 h-8 rounded-full bg-white/5 animate-pulse" />
                <div className="absolute bottom-1 right-2 text-[8px] font-black text-white/5 italic">PlayHub-9</div>
              </div>
              
              <div 
                className="absolute inset-0 backface-hidden [transform:rotateY(180deg)] flex items-center justify-center rounded-2xl border-2 shadow-2xl overflow-hidden"
                style={{ 
                  backgroundColor: `${card.color}22`, 
                  borderColor: isPreviewing ? 'rgba(255,255,255,0.4)' : card.color,
                  boxShadow: `0 0 25px ${card.color}44`
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent" />
                <i className={`fas ${card.icon} text-3xl md:text-4xl drop-shadow-lg`} style={{ color: card.color }}></i>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col items-center gap-2 mt-4">
        <div className="flex items-center gap-3 text-slate-500 dark:text-slate-500 text-[10px] font-black uppercase tracking-[0.3em] bg-slate-100 dark:bg-white/5 px-6 py-2 rounded-full border border-slate-200 dark:border-white/5 transition-colors">
          <i className="fas fa-brain text-teal-500 dark:text-teal-400"></i>
          <span className="dark:text-slate-400 text-slate-500 font-bold">Memorize before the flip!</span>
        </div>
      </div>
    </div>
  );
};

export default MemoryMatrix;
